export type DiffPart = { type: 'same' | 'removed' | 'added'; text: string }

/** Words, single punctuation marks and runs of whitespace, in order. */
const tokenize = (text: string) =>
	text.match(/\s+|[\p{L}\p{N}]+|[^\s\p{L}\p{N}]/gu) ?? []

/** Edits beyond this are a rewrite, not a correction: shown as one swap. */
const MAX_EDITS = 1500

/**
 * What changed between two versions of a text, word by word: the parts in
 * order, with each changed stretch as its removed text followed by its added
 * text. Built for reviewing a proofread — a few scattered edits in a long
 * text — so it uses Myers' diff, which is quick when the edits are few.
 */
export function diffWords(before: string, after: string): DiffPart[] {
	const a = tokenize(before)
	const b = tokenize(after)
	const ops = myers(a, b)
	if (!ops) {
		return [
			...(before ? [{ type: 'removed' as const, text: before }] : []),
			...(after ? [{ type: 'added' as const, text: after }] : []),
		]
	}

	// walk the edit script, collecting each changed stretch; a lone space
	// between two edits belongs to the stretch, so "a b" → "x y" reads as one
	const parts: DiffPart[] = []
	let removed = ''
	let added = ''
	const flush = () => {
		if (removed) parts.push({ type: 'removed', text: removed })
		if (added) parts.push({ type: 'added', text: added })
		removed = ''
		added = ''
	}
	let i = 0
	let j = 0
	for (let n = 0; n < ops.length; n++) {
		const op = ops[n]
		if (op === 'removed') removed += a[i++]
		else if (op === 'added') added += b[j++]
		else {
			const token = a[i]!
			const inChange = removed || added
			if (
				inChange &&
				/^[^\S\n]+$/.test(token) &&
				ops[n + 1] &&
				ops[n + 1] !== 'same'
			) {
				removed += token
				added += token
			} else {
				flush()
				const last = parts.at(-1)
				if (last?.type === 'same') last.text += token
				else parts.push({ type: 'same', text: token })
			}
			i++
			j++
		}
	}
	flush()
	return parts
}

type Op = 'same' | 'removed' | 'added'

/** Myers' O(ND) shortest edit script, or null past MAX_EDITS. */
function myers(a: string[], b: string[]): Op[] | null {
	const n = a.length
	const m = b.length
	const max = n + m
	// v[k] = furthest x on diagonal k; stored around an offset, one copy per
	// round (only the window that round can reach) for the backtrack
	const offset = max + 1
	const v = new Int32Array(2 * max + 3)
	const trace: Int32Array[] = []
	for (let d = 0; d <= Math.min(max, MAX_EDITS); d++) {
		trace.push(v.slice(offset - d - 1, offset + d + 2))
		for (let k = -d; k <= d; k += 2) {
			let x =
				k === -d || (k !== d && v[offset + k - 1]! < v[offset + k + 1]!)
					? v[offset + k + 1]!
					: v[offset + k - 1]! + 1
			let y = x - k
			while (x < n && y < m && a[x] === b[y]) {
				x++
				y++
			}
			v[offset + k] = x
			if (x >= n && y >= m) return backtrack(trace, n, m)
		}
	}
	return null
}

function backtrack(trace: Int32Array[], n: number, m: number): Op[] {
	const ops: Op[] = []
	let x = n
	let y = m
	for (let d = trace.length - 1; d >= 0; d--) {
		const window = trace[d]!
		const at = (k: number) => window[k + d + 1]!
		const k = x - y
		const prevK = k === -d || (k !== d && at(k - 1) < at(k + 1)) ? k + 1 : k - 1
		const prevX = at(prevK)
		const prevY = prevX - prevK
		while (x > prevX && y > prevY) {
			ops.push('same')
			x--
			y--
		}
		if (d > 0) ops.push(x === prevX ? 'added' : 'removed')
		x = prevX
		y = prevY
	}
	return ops.reverse()
}
